import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import configService from '../services/config';
import {
  CircularProgress,
  Box,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  useMediaQuery,
  useTheme,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  FormControl,
  Select,
  MenuItem
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import TimerIcon from '@mui/icons-material/Timer';
import VisibilityIcon from '@mui/icons-material/Visibility';

// ---------- Helpers ----------

// Safely parse "YYYY-MM-DD HH:mm:ss" style strings (space instead of "T"
// breaks Date parsing in some browsers), and normal ISO strings too.
const parseDate = (value) => {
  if (!value) return null;
  const normalized = typeof value === 'string' ? value.replace(' ', 'T') : value;
  const d = new Date(normalized);
  return isNaN(d.getTime()) ? null : d;
};

// Format time from ISO / "YYYY-MM-DD HH:mm:ss" string
const fmt = (iso) => {
  const d = parseDate(iso);
  return d ? d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }) : '—';
};

// Format date for display (e.g., "07 Jul, 2026")
const fmtDate = (iso) => {
  const d = parseDate(iso);
  return d ? d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
};

// Calculate duration between in-time and out-time (e.g., "2 HOURS", "45 MIN", "1 HOUR 20 MIN")
const calcDuration = (inTime, outTime) => {
  const inDate = parseDate(inTime);
  const outDate = parseDate(outTime);
  if (!inDate || !outDate) return 0;

  const diffMs = outDate - inDate;
  if (diffMs <= 0) return 0;

  const totalMinutes = Math.round(diffMs / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours === 0) return `${minutes} MIN`;
  if (minutes === 0) return `${hours} HOUR${hours === 1 ? '' : 'S'}`;
  return `${hours} HOUR${hours === 1 ? '' : 'S'} ${minutes} MIN`;
};

// ---------- Main Component ----------
export default function Attendance() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { company_id, userId } = useSelector((state) => state.globalVariables);

  // Main attendance list (used for the table / cards)
  const [attendanceList, setAttendanceList] = useState([]);
  const [loading, setLoading] = useState(true);

  // States for Filters
  const [filter, setFilter] = useState('All');
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7)); // 'YYYY-MM' format

  // Site-wise modal (opened from the eye button)
  const [openSiteModal, setOpenSiteModal] = useState(false);
  const [siteData, setSiteData] = useState([]);
  const [siteLoading, setSiteLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);

  // ---------- Fetch main attendance ----------
  // const fetchAttendance = async () => {
  //   try {
  //     const res = await configService.getAttendanceByDateRange({ company_id, employee_id: userId });
  //     if (res?.status || res?.data?.status) {
  //       const rawData = res.data?.data || res.data || [];
  //       setAttendanceList(rawData);
  //     }
  //   } catch (error) {
  //     console.error('Fetch Attendance Error:', error);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // useEffect(() => {
  //   fetchAttendance();
  // }, [userId]);

  const fetchAttendance = async () => {
    setLoading(true);

    try {
      const [year, month] = selectedMonth.split("-");

      const from_date = `${year}-${month}-01`;

      const to_date = new Date(year, month, 0)
        .toISOString()
        .split("T")[0];

      const res = await configService.getAttendanceByDateRange({
        company_id,
        employee_id: userId,
        from_date,
        to_date,
        status: filter,
      });
    
      if (res?.status || res?.data?.status) {
        const rawData = res.data?.data || res.data || [];
        setAttendanceList(rawData);
      }
    } catch (error) {
      console.error("Fetch Attendance Error:", error);
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    if (userId) {
      fetchAttendance();
    }
  }, [userId, selectedMonth, filter]);





  // ---------- Fetch site-wise attendance for a given attendance id ----------
  const fetchSiteAttendance = async (id) => {
    setSiteLoading(true);
    try {
      const res = await configService.getAttendanceById({ id });
      // API can return { data: { data: [...] } } or { data: [...] } or just [...]
      const rawData = res?.data?.data || res?.data || res || [];
      return Array.isArray(rawData) ? rawData : [];
    } catch (error) {
      console.error('Fetch Site Attendance Error:', error);
      return [];
    } finally {
      setSiteLoading(false);
    }
  };

  // ---------- Modal handlers (eye button) ----------
  const handleOpenSiteModal = async (item) => {
    setSelectedDate(item.date);
    setSiteData([]);
    setOpenSiteModal(true);

    const sites = await fetchSiteAttendance(item.attendance_id);
    setSiteData(sites);
  };

  const handleCloseSiteModal = () => {
    setOpenSiteModal(false);
    setSiteData([]);
    setSelectedDate(null);
  };

  // ---------- Render ----------
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress sx={{ color: '#6c5ce7' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, color: '#1e293b', minHeight: '100vh', bgcolor: '#f8fafc' }}>
      {/* ── Summary Cards ── */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, bgcolor: '#ffffff', borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'rgba(22, 163, 74, 0.1)', color: '#16a34a', display: 'flex' }}>
              <CalendarMonthIcon />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#16a34a' }}>
                {attendanceList.length}
              </Typography>
              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500 }}>Total Days</Typography>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, bgcolor: '#ffffff', borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'rgba(79, 142, 247, 0.1)', color: '#4f8ef7', display: 'flex' }}>
              <TimerIcon />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#4f8ef7' }}>
                {attendanceList[0]?.duration || '0 hr'}
              </Typography>
              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500 }}>Last Duration</Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* ── Main Section ── */}
      <Paper sx={{ p: { xs: 2, md: 3 }, bgcolor: '#ffffff', borderRadius: 4, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>Attendance Records</Typography>
            <Typography variant="body2" sx={{ color: '#64748b' }}>{attendanceList.length} Records found</Typography>
          </Box>

          {/* ── Month Picker & Dropdown Filters ── */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              type="month"
              size="small"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              sx={{
                bgcolor: '#ffffff',
                '& .MuiOutlinedInput-root': {
                  color: '#1e293b',
                  '& fieldset': { borderColor: '#cbd5e1' },
                  '&:hover fieldset': { borderColor: '#94a3b8' },
                  '&.Mui-focused fieldset': { borderColor: '#6c5ce7' },
                }
              }}
            />

            <FormControl size="small" sx={{ minWidth: 120, bgcolor: '#ffffff' }}>
              <Select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                displayEmpty
                sx={{
                  color: '#1e293b',
                  '.MuiOutlinedInput-notchedOutline': { borderColor: '#cbd5e1' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#94a3b8' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6c5ce7' },
                  '.MuiSvgIcon-root': { color: '#64748b' }
                }}
              >
                <MenuItem value="All">All</MenuItem>
                <MenuItem value="Present">Present</MenuItem>
                <MenuItem value="Absent">Absent</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        {attendanceList.length === 0 ? (
          <Typography align="center" sx={{ py: 10, color: '#94a3b8' }}>
            No records found
          </Typography>
        ) : isMobile ? (
          /* ── Mobile View ── */
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {attendanceList.map((item, idx) => (
              <Card key={idx} sx={{ bgcolor: '#ffffff', border: '1px solid #e2e8f0', color: '#1e293b', boxShadow: 'none', borderRadius: 3 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ color: '#475569', fontWeight: 600 }}>
                      {fmtDate(item.date)}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Chip label="Present" size="small" sx={{ bgcolor: 'rgba(22, 163, 74, 0.1)', color: '#16a34a', fontWeight: 600 }} />
                      <IconButton
                        size="small"
                        onClick={() => handleOpenSiteModal(item)}
                        sx={{ color: '#6c5ce7', bgcolor: 'rgba(108, 92, 231, 0.08)', '&:hover': { bgcolor: 'rgba(108, 92, 231, 0.15)' } }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Typography variant="caption" display="block" color="#94a3b8" sx={{ fontWeight: 500 }}>
                        IN / OUT
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#334155' }}>
                        {fmt(item.punch_in)} - {fmt(item.punch_out)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sx={{ textAlign: 'right' }}>
                      <Typography variant="caption" display="block" color="#94a3b8" sx={{ fontWeight: 500 }}>
                        Duration
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#4f8ef7', fontWeight: 600 }}>
                        {item.duration || '—'}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            ))}
          </Box>
        ) : (
          /* ── Desktop View ── */
          <TableContainer>
            <Table sx={{ '& .MuiTableCell-root': { color: '#334155', borderColor: '#e2e8f0' } }}>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  <TableCell sx={{ color: '#475569', fontWeight: 600 }}>#</TableCell>
                  <TableCell sx={{ color: '#475569', fontWeight: 600 }}>Date</TableCell>
                  <TableCell sx={{ color: '#475569', fontWeight: 600 }}>Punch In</TableCell>
                  <TableCell sx={{ color: '#475569', fontWeight: 600 }}>Punch Out</TableCell>
                  <TableCell sx={{ color: '#475569', fontWeight: 600 }}>Duration</TableCell>
                  <TableCell sx={{ color: '#475569', fontWeight: 600 }}>Status</TableCell>
                  <TableCell align="center" sx={{ color: '#475569', fontWeight: 600 }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {attendanceList.map((item, idx) => (
                  <TableRow key={idx} sx={{ '&:hover': { bgcolor: '#f8fafc' }, transition: 'background-color 0.2s' }}>
                    <TableCell sx={{ color: '#94a3b8' }}>{idx + 1}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#1e293b' }}>{fmtDate(item.date)}</TableCell>
                    <TableCell>{fmt(item.punch_in)}</TableCell>
                    <TableCell>{fmt(item.punch_out)}</TableCell>
                    <TableCell sx={{ color: '#4f8ef7', fontWeight: 500 }}>{item.duration}</TableCell>
                    <TableCell>
                      <Chip label="Present" sx={{ bgcolor: 'rgba(22, 163, 74, 0.1)', color: '#16a34a', fontWeight: 600, borderRadius: 1.5 }} />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenSiteModal(item)}
                        sx={{ color: '#6c5ce7', bgcolor: 'rgba(108, 92, 231, 0.08)', '&:hover': { bgcolor: 'rgba(108, 92, 231, 0.15)' } }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* ── SITE‑WISE MODAL (opened via the eye button) ── */}
      <Dialog
        open={openSiteModal}
        onClose={handleCloseSiteModal}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        scroll="paper"
        PaperProps={{
          sx: {
            bgcolor: '#ffffff',
            border: { xs: 'none', sm: '1px solid #e2e8f0' },
            color: '#1e293b',
            borderRadius: { xs: 0, sm: 4 },
            m: { xs: 0, sm: 3 },
            maxHeight: { xs: '100%', sm: 'calc(100% - 64px)' },
            width: { xs: '100%', sm: 'auto' },
          },
        }}
      >
        <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography
            variant="h6"
            sx={{
              mb: 2,
              fontWeight: 600,
              color: '#1e293b',
              fontSize: { xs: '1rem', sm: '1.25rem' },
              wordBreak: 'break-word',
            }}
          >
            Site-wise Attendance – {selectedDate ? fmtDate(selectedDate) : ''}
          </Typography>

          {siteLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress sx={{ color: '#6c5ce7' }} />
            </Box>
          ) : siteData.length === 0 ? (
            <Typography align="center" sx={{ py: 4, color: '#94a3b8' }}>
              No site data available for this date.
            </Typography>
          ) : (
            <TableContainer
              component={Paper}
              sx={{
                bgcolor: '#ffffff',
                border: '1px solid #e2e8f0',
                boxShadow: 'none',
                width: '100%',
                overflowX: 'hidden',
              }}
            >
              <Table
                size="small"
                sx={{
                  width: '100%',
                  tableLayout: { xs: 'fixed', sm: 'auto' },
                  '& .MuiTableCell-root': {
                    color: '#1e293b',
                    borderColor: '#e2e8f0',
                    px: { xs: 0.75, sm: 2 },
                    py: { xs: 0.75, sm: 1.25 },
                    fontSize: { xs: '0.68rem', sm: '0.875rem' },
                    whiteSpace: { xs: 'normal', sm: 'nowrap' },
                    wordBreak: { xs: 'break-word', sm: 'normal' },
                    lineHeight: { xs: 1.3, sm: 1.5 },
                  },
                }}
              >
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8fafc' }}>
                    <TableCell sx={{ fontWeight: 'bold', color: '#475569', width: { xs: '32%', sm: 'auto' } }}>
                      {selectedDate
                        ? new Date(selectedDate).toLocaleDateString('en-US', { weekday: 'long' }).toUpperCase()
                        : 'SITE'}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: '#475569', width: { xs: '23%', sm: 'auto' } }}>IN TIME</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: '#475569', width: { xs: '23%', sm: 'auto' } }}>OUT TIME</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: '#475569', width: { xs: '22%', sm: 'auto' } }}>DURATION</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {siteData.map((site, idx) => (
                    <TableRow key={site.site_id ?? idx} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                      <TableCell sx={{ fontWeight: 600 }}>{site.site_name || `Site ${site.site_id ?? idx + 1}`}</TableCell>
                      <TableCell>{site.in?.time ? fmt(site.in.time) : 0}</TableCell>
                      <TableCell>{site.out?.time ? fmt(site.out.time) : 0}</TableCell>
                      <TableCell sx={{ color: '#4f8ef7', fontWeight: 500 }}>{calcDuration(site.in?.time, site.out?.time)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, pt: 0 }}>
          <Button
            onClick={handleCloseSiteModal}
            fullWidth={isMobile}
            sx={{ color: '#64748b', '&:hover': { color: '#1e293b', bgcolor: '#f8fafc' } }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}